import { useLocation, useInRouterContext } from 'react-router';
import type { UrlObject } from '../types/router';
import { useCallback, useContext } from 'react';
import { EmberServicesContext } from '../contexts/ember-services-context';
/**
 * Determines whether the current route is handled by the Ember router.
 *
 * This hook inspects the application's router to check if the current path
 * matches any Ember route handler, excluding those that contain "react" in their name.
 * We need to apply a regex to explicitly exclude 'react' routes because,
 * if not, 'react-fallback' and similar named routes would match.
 * @returns {boolean} True if the current route is an Ember route (not a React route), otherwise false.
 * @remarks
 * This is useful for hybrid applications where both Ember and React routes may coexist,
 * and you need to conditionally render or handle logic based on the routing system in use.
 */
export const useIsEmberRoute = (): ((pathname?: string | UrlObject) => boolean) => {
  const emberServicesContext = useContext(EmberServicesContext);

  if (!emberServicesContext || !emberServicesContext.emberRouter) {
    throw new Error('useIsEmberRoute hook can only be used with EmberServicesProvider');
  }

  // if used outside a react <Router /> context we assume Ember for backward compatibility
  if (!useInRouterContext()) {
    return useCallback(() => true, []);
  }

  const { emberRouter } = emberServicesContext;
  const location = useLocation();

  return useCallback(
    (path?: string | UrlObject) => {
      // if inspection isn't possible, we assume Ember for backward compatibility
      if (!emberRouter.recognize) return true;

      const targetPath = typeof path === 'string' ? path : path?.pathname || location.pathname;
      const routeName = emberRouter.recognize(targetPath).name;
      return Boolean(routeName) && !/react/.exec(routeName);
    },
    [emberRouter, location.pathname]
  );
};
